/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Chip } from '@mui/material';
import React from 'react';
import { CustomTooltip } from '@opengeoweb/shared';
import type { DayRange } from './daySelectorUtils';
import {
  CHIP_SIZE,
  dayOptions,
  getDateForDay,
  isDayInRange,
} from './daySelectorUtils';
import { useDaySelection } from './useDaySelection';
import { formatDayHeader } from '../WarningEventTimeline';

export interface DaySelectorProps {
  selectedDays: DayRange;
  onChange: (range: DayRange | null) => void;
  isDisabled?: boolean;
  now?: Date;
}

export const DaySelector: React.FC<DaySelectorProps> = ({
  selectedDays,
  onChange,
  isDisabled = false,
  now = new Date(),
}) => {
  const {
    containerRef,
    focusedDay,
    previewRange,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onKeyDown,
    onChipFocus,
  } = useDaySelection({ selectedDays, onChange, isDisabled });

  const chipRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const shownRange = previewRange ?? selectedDays;

  // follow the focused day with the focus ring, but only once the strip already
  // holds focus, so a reset from the form never steals it from another field
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container?.contains(document.activeElement)) {
      return;
    }
    chipRefs.current[focusedDay]?.focus();
  }, [focusedDay, containerRef]);

  return (
    <Box
      aria-label="Warning day range"
      onPointerCancel={onPointerCancel}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      ref={containerRef}
      role="group"
      sx={{
        display: 'flex',
        width: CHIP_SIZE * dayOptions.length,
        paddingTop: '12px',
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      {dayOptions.map((day) => {
        const isSelected = isDayInRange(day.value, shownRange);
        return (
          <CustomTooltip
            key={day.value}
            placement="bottom"
            title={formatDayHeader(getDateForDay(day.value, now))}
          >
            <Chip
              aria-pressed={isSelected}
              clickable={!isDisabled}
              color={isSelected ? 'primary' : 'default'}
              data-testid={`warning-day-chip-${day.value}`}
              disabled={isDisabled}
              label={day.label}
              onFocus={() => onChipFocus(day.value)}
              onKeyDown={(event) => onKeyDown(day.value, event)}
              onPointerDown={(event) => onPointerDown(day.value, event)}
              ref={(node: HTMLDivElement | null) => {
                chipRefs.current[day.value] = node;
              }}
              size="small"
              sx={{
                borderRadius: 1,
                height: CHIP_SIZE,
                width: CHIP_SIZE,
                borderColor: 'transparent',
                mr: 0,
                ...(day.value === 0 && {
                  fontSize: 8,
                  '.MuiChip-label': { paddingX: 0 },
                }),
              }}
              tabIndex={day.value === focusedDay ? 0 : -1}
              variant={isSelected ? 'filled' : 'outlined'}
            />
          </CustomTooltip>
        );
      })}
    </Box>
  );
};
